import ChatAxios from './api/chatAxios';
import { AxiosResponse } from 'axios';
import { UserInfoFormData, MessageFormData } from '../components/ChatSupport/validationSchemas';

export interface ChatSupportResponse<T = any> {
  success: boolean;
  data: T | null;
  error: string | null;
}

export interface ChatSession {
  session_id: string;
  userId?: string;
  userName?: string;
  userEmail?: string;
  createdAt?: string;
  status?: 'active' | 'closed' | 'pending';
}

export interface ChatMessage {
  id: string;
  sessionId: string;
  message: string;
  sender: 'user' | 'support';
  timestamp: string;
  isRead: boolean;
}

export interface SendMessageResponse {
  message: ChatMessage;
  supportResponse?: ChatMessage;
}

const extractSessionId = (payload: any): string | null => {
  if (!payload) return null;
  return (
    payload.session_id ||
    payload.sessionId ||
    payload.id ||
    payload?.data?.session_id ||
    payload?.data?.sessionId ||
    payload?.data?.id ||
    null
  );
};

const normalizeSessionResponse = (payload: any): ChatSession | null => {
  const sessionId = extractSessionId(payload);
  if (!sessionId) return null;

  const source = payload?.data && typeof payload.data === 'object' ? payload.data : payload;
  return {
    session_id: sessionId,
    userId: source?.userId || source?.user_id,
    userName: source?.userName || source?.user_name || source?.customer_name,
    userEmail: source?.userEmail || source?.user_email || source?.customer_email,
    createdAt: source?.createdAt || source?.created_at,
    status: source?.status,
  };
};

export interface ChatMessageResponse {
  message_id: string;
  from_user: 'system' | 'agent' | 'customer';
  message: string;
  timestamp: string;
  sender_name: string;
  sender_id: string | null;
  is_root: boolean;
}

export interface ChatMessagesResponse {
  session_id: string;
  messages: ChatMessageResponse[];
  total_count: number;
  customer_email: string;
  customer_name: string;
}

const normalizeMessagesResponse = (payload: any, sessionId: string): ChatMessagesResponse => {
  if (Array.isArray(payload)) {
    return {
      session_id: sessionId,
      messages: payload,
      total_count: payload.length,
      customer_email: '',
      customer_name: '',
    };
  }

  if (Array.isArray(payload?.messages)) {
    return {
      session_id: payload.session_id || sessionId,
      messages: payload.messages,
      total_count: payload.total_count ?? payload.messages.length,
      customer_email: payload.customer_email || '',
      customer_name: payload.customer_name || '',
    };
  }

  if (Array.isArray(payload?.data)) {
    return {
      session_id: payload.session_id || sessionId,
      messages: payload.data,
      total_count: payload.total_count ?? payload.data.length,
      customer_email: payload.customer_email || '',
      customer_name: payload.customer_name || '',
    };
  }

  if (Array.isArray(payload?.data?.messages)) {
    return {
      session_id: payload?.data?.session_id || sessionId,
      messages: payload.data.messages,
      total_count: payload?.data?.total_count ?? payload.data.messages.length,
      customer_email: payload?.data?.customer_email || '',
      customer_name: payload?.data?.customer_name || '',
    };
  }

  return {
    session_id: sessionId,
    messages: [],
    total_count: 0,
    customer_email: '',
    customer_name: '',
  };
};

export const initializeChatSession = async (
  userInfo: UserInfoFormData
): Promise<ChatSupportResponse<ChatSession>> => {
  const payload = {
    ...userInfo,
    room_key: userInfo.room_key || process.env.REACT_APP_CHAT_ROOM_KEY || 'lottoreco_chat',
  };

  console.log('ChatSupportService: Initializing chat session for user:', payload);

  try {
    const response = await ChatAxios.post('/chat/sessions', payload) as AxiosResponse;
    const normalizedSession = normalizeSessionResponse(response.data);

    if (!normalizedSession) {
      return {
        success: false,
        data: null,
        error: 'Session ID not found in create session response'
      };
    }

    return {
      success: true,
      data: normalizedSession,
      error: null
    };
  } catch (error: any) {
    return {
      success: false,
      data: null,
      error: error.response?.data?.message || error.message || 'Failed to initialize chat session'
    };
  }
};

export const sendMessage = async (
  sessionId: string,
  messageData: MessageFormData
): Promise<ChatSupportResponse<SendMessageResponse>> => {
  try {
    const response = await ChatAxios.post(`/sessions/${sessionId}/messages`, messageData) as AxiosResponse;
    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error: any) {
    return {
      success: false,
      data: null,
      error: error.response?.data?.message || error.message || 'Failed to send message'
    };
  }
};

export const getChatMessages = async (
  sessionId: string
): Promise<ChatSupportResponse<ChatMessagesResponse>> => {
  try {
    const response = await ChatAxios.get(`/sessions/${sessionId}/messages`) as AxiosResponse;
    return {
      success: true,
      data: normalizeMessagesResponse(response.data, sessionId),
      error: null
    };
  } catch (error: any) {
    return {
      success: false,
      data: null,
      error: error.response?.data?.message || error.message || 'Failed to get messages'
    };
  }
};

export const closeChatSession = async (
  sessionId: string
): Promise<ChatSupportResponse<{ message: string }>> => {
  try {
    const response = await ChatAxios.put(`/sessions/${sessionId}/close`, {}) as AxiosResponse;
    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error: any) {
    return {
      success: false,
      data: null,
      error: error.response?.data?.message || error.message || 'Failed to close chat session'
    };
  }
};
